import { useCallback, useEffect, useRef } from "react";
import type { FormApi } from "./types";

export interface UseLoadedFormValuesParams<TValues> {
  /** The panel's own `open` flag. Resets the applied-once guard when it drops. */
  open: boolean;
  /**
   * Whether `values` is the finished, server-loaded object rather than the empty
   * placeholder the panel mounted with. Typically `!isLoading && !isFetching`.
   */
  ready: boolean;
  /**
   * The values to push once `ready` first turns true.
   *
   * Partial by design: a create flow often needs to seed only the part of the form
   * that depends on a server read (the attribute rows, say) and leave the rest of
   * the defaults alone.
   */
  values: Partial<TValues>;
  /**
   * The panel's `key`, when it has one.
   *
   * Values are applied once per open, so a keyed panel that is remounted *without*
   * this component remounting — swapping which entity is being edited while the
   * panel stays open — would otherwise keep the spent guard and never seed the new
   * form. Passing the same value used as the panel's `key` re-arms it.
   */
  resetOn?: string;
}

/**
 * Push server-loaded values into an already-mounted `FormSlidePanel`.
 *
 * `useResetOnOpen` only reseeds a panel on the closed→open edge, and these panels
 * mount *already open* (their parents gate on `useSlidePanelState().isMounted`) with
 * their reads still in flight — so the reset lands on the placeholder values and
 * later `initialValues` never reach the form.
 *
 * The tempting fix is `key={JSON.stringify(initialValues)}`, and it is wrong: the
 * key changes while the panel is mid-slide-in, `SlidePanel` remounts, and
 * `usePanelTransition` replays the enter animation from off-screen. It also
 * stringifies the whole value object on every render.
 *
 * Instead, keep the panel keyed on the *entity* (or unkeyed) and hand its
 * `setFormRef` to the returned callback. Values are applied exactly once per open
 * via `setProgrammaticValues`, which does not mark fields dirty or touched and
 * preserves anything the user already typed (`keepDirtyValues`) — so a late refetch
 * cannot clobber an edit in progress.
 *
 * @example
 * const setFormRef = useLoadedFormValues({ open, ready: !isLoading, values: initialValues });
 * return <FormSlidePanel key={id ?? "new"} open={open} setFormRef={setFormRef} … />;
 */
// Unconstrained `TValues`: form-value shapes in this repo are declared as
// interfaces, which do not satisfy `Record<string, unknown>` (no implicit index
// signature). `FormApi<TValues>` does not constrain it either.
export function useLoadedFormValues<TValues>({
  open,
  ready,
  values,
  resetOn,
}: UseLoadedFormValuesParams<TValues>): (api: FormApi<TValues>) => void {
  const formRef = useRef<FormApi<TValues> | null>(null);
  const appliedRef = useRef(false);

  // Read through a ref so a new `values` identity does not re-trigger the effect
  // (these objects are rebuilt on most renders); `ready` is the only trigger.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const setFormRef = useCallback((api: FormApi<TValues>) => {
    formRef.current = api;
  }, []);

  // Re-arm before the guard is read, so a `resetOn` change and a `ready` that is
  // already true in the same commit still seed the newly mounted form.
  const resetOnRef = useRef(resetOn);
  if (resetOnRef.current !== resetOn) {
    resetOnRef.current = resetOn;
    appliedRef.current = false;
  }

  useEffect(() => {
    if (!open) {
      appliedRef.current = false;
      return;
    }
    if (!ready || appliedRef.current) return;
    // `FormSlidePanel` calls `setFormRef` from its own effect, and child effects
    // flush before the parent's — so on the mount commit the ref is already set.
    const api = formRef.current;
    if (!api) return;
    appliedRef.current = true;
    api.setProgrammaticValues(valuesRef.current);
  }, [open, ready, resetOn]);

  return setFormRef;
}

export default useLoadedFormValues;
